import type { MedRequest } from '@/types/data'

const getRoles = () => {
  return window?.Clerk?.user?.publicMetadata.roles ?? []
}

export function isRoleAllowedToEditMedRequest(medRequest: MedRequest) {
  const roles = getRoles()

  const isPublicOrder = medRequest.OTC || medRequest.epharmacyVendor === '7a99527ae705fe76a25f1d70cf802b'

  if (roles.includes('PHARMACIST') && isPublicOrder) {
    return true
  }

  if (
    (['LEAD_PRESCRIBER', 'PRESCRIBER', 'JUNIOR_PRESCRIBER'] as const).some(role => roles.includes(role)) &&
    isPublicOrder
  ) {
    return false
  }

  const nonEditingRoles: ClerkUserRole[] = ['VIEWER', 'CS-VIEWER', 'DIETITIAN']
  if (roles.some(role => nonEditingRoles.includes(role))) return false

  return true
}

export function isRoleLimitedToEditingNotes() {
  return getRoles().includes('DIETITIAN')
}

export function isRoleAllowedToEditMedRequestNotes(medRequest: MedRequest) {
  return isRoleLimitedToEditingNotes() || isRoleAllowedToEditMedRequest(medRequest)
}

export function isRoleAllowedToActionMedRequest(medRequest?: MedRequest) {
  const roles = getRoles()

  if (!medRequest) return false

  if (roles.includes('CS-VIEWER')) return false

  const isPublicOrder = medRequest.orderProcessType === 'public'
  return roles.includes('PHARMACIST') ? isPublicOrder || medRequest?.OTC : true
}

export function isRoleAllowedToEditPatientData() {
  const roles = getRoles()

  const allowedRoles: ClerkUserRole[] = [
    'PRESCRIBER',
    'LEAD_PRESCRIBER',
    'PHARMACIST',
    'DIETITIAN',
    'JUNIOR_PRESCRIBER'
  ]
  return roles.some(role => allowedRoles.includes(role))
}

export function isRoleAllowedToUseDecisionAssistant() {
  const roles = getRoles()

  const allowedRoles: ClerkUserRole[] = [
    'DA_MANAGER',
    'DA_VIEWER',
    'LEAD_PRESCRIBER',
    'PRESCRIBER',
    'JUNIOR_PRESCRIBER',
    'PHARMACIST'
  ]
  return roles.some(role => allowedRoles.includes(role))
}

export function isRoleAllowedToMarkFileViewed() {
  const roles = getRoles()

  // Mirrors the NodeAPI PA_MARK_FILE_VIEWED guard on PATCH /patients/:hash/files/:id/viewed —
  // any other role gets a 403, so we never render the control for them.
  const allowedRoles: ClerkUserRole[] = [
    'LEAD_PRESCRIBER',
    'PRESCRIBER',
    'JUNIOR_PRESCRIBER',
    'TRIAL_PRESCRIBER',
    'PHARMACIST'
  ]
  return roles.some(role => allowedRoles.includes(role))
}

export function isRoleAllowedToSeeFooterInInvestigation() {
  const roles = getRoles()

  const nonAllowedRoles: ClerkUserRole[] = ['PHARMACIST', 'VIEWER', 'DIETITIAN']
  return !roles.some(role => nonAllowedRoles.includes(role))
}

export function isRoleAllowedToEditInvestigations() {
  const roles = getRoles()

  const nonAllowedRoles: ClerkUserRole[] = ['PHARMACIST', 'VIEWER', 'CS-VIEWER', 'DIETITIAN']
  return !roles.some(role => nonAllowedRoles.includes(role))
}

export function shouldLockMedRequest(medRequest: MedRequest) {
  const roles = getRoles()

  if (roles.includes('VIEWER') || roles.includes('CS-VIEWER') || roles.includes('DIETITIAN')) return false

  if (roles.includes('LEAD_PRESCRIBER')) {
    return !medRequest.OTC && medRequest.orderProcessType !== 'public'
  }

  if (roles.includes('PRESCRIBER')) {
    return !medRequest.OTC && !medRequest.trainingMode && medRequest.orderProcessType !== 'public'
  }

  if (roles.includes('JUNIOR_PRESCRIBER')) {
    return (
      !medRequest.OTC &&
      !medRequest.trainingMode &&
      !medRequest.juniorPrescriberRejected &&
      medRequest.orderProcessType !== 'public'
    )
  }

  if (roles.includes('PHARMACIST')) {
    return medRequest.OTC || medRequest.orderProcessType === 'public'
  }

  return true
}

export function shouldLockInvestigation() {
  const roles = getRoles()
  if (
    roles.includes('VIEWER') ||
    roles.includes('CS-VIEWER') ||
    roles.includes('PHARMACIST') ||
    roles.includes('DIETITIAN')
  )
    return false
  return true
}

export function isUserTrialPrescriber() {
  return getRoles().includes('TRIAL_PRESCRIBER')
}

export function isUserJuniorPrescriber() {
  return getRoles().includes('JUNIOR_PRESCRIBER')
}

export function preloadManyItems() {
  const roles = getRoles()
  return roles.some(role => ['PHARMACIST', 'TRIAL_PRESCRIBER', 'JUNIOR_PRESCRIBER'].includes(role))
}

export function canSearchOrdersByCustomer() {
  const roles = getRoles()

  // Name/email search reads GET /orders, which NodeAPI guards with CS_VIEW_ALL — every role here holds it.
  const allowedRoles: ClerkUserRole[] = ['PHARMACIST', 'CS_PHARMACY_MANAGER', 'LEAD_PRESCRIBER']
  return roles.some(role => allowedRoles.includes(role))
}

export function isCSViewer() {
  const roles = getRoles()
  return roles.includes('CS-VIEWER')
}

export function canViewConversations() {
  const roles = getRoles()
  return (
    roles.includes('CS-VIEWER') ||
    roles.includes('VIEWER') ||
    roles.includes('LEAD_PRESCRIBER') ||
    roles.includes('PRESCRIBER') ||
    roles.includes('JUNIOR_PRESCRIBER') ||
    roles.includes('PHARMACIST') ||
    roles.includes('DIETITIAN')
  )
}

export function canSendMessages() {
  const roles = getRoles()
  return !roles.includes('CS-VIEWER') && !roles.includes('VIEWER')
}

export function canEditMessages() {
  const roles = getRoles()
  return !roles.includes('CS-VIEWER') && !roles.includes('VIEWER')
}

export function canViewConversationsWhenLocked() {
  const roles = getRoles()
  return roles.includes('CS-VIEWER') || roles.includes('VIEWER')
}
